import { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import {
  AccessorySize,
  Field,
  FormFieldStatus,
  Glyph,
  Hint,
  Icon,
  Overflow,
  Select,
} from "@gocardless/flux-react";
import { useLingui } from "@lingui/react";

import { SignUpEvents, SignupFormData } from "../../consts";
import { getSupportedCountries } from "../../helper";

import { CountryOptions } from "./CountryOptions";

import { useSupportedCountries } from "src/libraries/creditor/hooks";
import {
  CountryCodes,
  CountryCodeSupported,
  countryCodeToName,
} from "src/common/country";
import { useSegment } from "src/technical-integrations/segment/useSegment";

const styles = {
  field: {
    "div div": {
      left: "20px",
    },
    "& [data-field-control]": {
      marginTop: "4px !important",
    },
    select: {
      paddingLeft: "40px",
      fontSize: "0.875rem",
    },
    svg: {
      right: "9.6px",
    },
  },
  select: {
    boxShadow: "0 0 0 1px #8c8579",
    borderTopLeftRadius: "0px",
    borderBottomLeftRadius: "0px",
    overflowX: Overflow.Hidden,
    "&:focus": {
      outline: "none",
      boxShadow: `0 0 0 2px #333333`,
    },
  },
};
interface CountryDropdownProps {
  userCountryCode: string;
}

export const CountryDropdown: React.FC<CountryDropdownProps> = ({
  userCountryCode,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const countryRef = useRef<HTMLSelectElement | null>(null);
  const { sendEvent } = useSegment();
  const {
    register,
    formState: { errors },
    setValue,
  } = useFormContext<SignupFormData>();
  const { i18n } = useLingui();

  const { supportedCountries, isLoading } = useSupportedCountries();
  const countriesData = getSupportedCountries(i18n, supportedCountries);
  const defaultBusinessLocation = countriesData.find(
    (c) => c.name === countryCodeToName(i18n)[userCountryCode as CountryCodes]
  );

  const [selectedLocationFlag, setSelectedLocationFlag] = useState<
    Glyph | undefined
  >(defaultBusinessLocation?.flag);

  // check if the user's cloudflare country exists in our
  // list of supported countries
  const country = defaultBusinessLocation?.code || CountryCodeSupported.GB;

  useEffect(() => {
    if (!isLoading && !isReady) {
      setValue("geo", country);
      setSelectedLocationFlag(
        defaultBusinessLocation?.flag || (`FlagRectGB` as Glyph)
      );
      setIsReady(true);
    }
  }, [country, defaultBusinessLocation, isLoading, setValue, isReady]);

  const handleOnFocus = () => setIsOpen(true);

  const handleLostFocus = () => {
    countryRef.current?.blur();
    setIsOpen(false);
  };

  return (
    <Field data-testid="geo" css={styles.field}>
      <Select
        id="geo"
        data-testid="geo-select"
        aria-label="Choose a country"
        {...(selectedLocationFlag && {
          leftAccessory: <Icon name={selectedLocationFlag} size="20px" />,
          leftAccessorySize: AccessorySize.Md,
        })}
        css={styles.select}
        {...register("geo", {
          onBlur: handleOnFocus,
          onChange: (e) => {
            handleLostFocus();
            const selectedOption = countriesData?.find(
              (c) => c.code === e.currentTarget.value
            );
            setSelectedLocationFlag(selectedOption?.flag);
            sendEvent(SignUpEvents.BusinessLocationChanged, {
              business_location: e.currentTarget.value,
            });
          },
        })}
        onBlur={handleLostFocus}
      >
        <CountryOptions countriesData={countriesData} isOpen={isOpen} />
      </Select>
      {errors.geo && (
        <Hint status={FormFieldStatus.Danger}>{errors.geo.message}</Hint>
      )}
    </Field>
  );
};
